import { Box, BoxProps, Collapse } from '@chakra-ui/react';
import { useState } from 'react';

import { Country } from '../gql/generated/graphql';
import { BasePreviousResidenceInfo, PrimaryResidenceYearsMonths } from '../pages/LoanApp/utils';
import TextWithTooltip from './TextWithTooltip';
import { AddressInput, Label } from './formComponents';
import MonthsAndYears, { shouldRequirePrevious } from './monthsAndYears/MonthsAndYears';

export type TimeAtAddressValues = {
  [K in keyof (PrimaryResidenceYearsMonths &
    BasePreviousResidenceInfo)]: (PrimaryResidenceYearsMonths & BasePreviousResidenceInfo)[K];
} & {
  [K in keyof (PrimaryResidenceYearsMonths &
    BasePreviousResidenceInfo) as `cobuyer_${K}`]?: (PrimaryResidenceYearsMonths &
    BasePreviousResidenceInfo)[K];
};

type TimeAtAddressProps = {
  values: TimeAtAddressValues;
  isCobuyer?: boolean;
  firstName?: string;
  title?: string;
  skipPreviousAddress?: boolean;
  country: Country;
};

const TimeAtAddress = ({
  values,
  isCobuyer = false,
  firstName,
  title = 'TIME AT CURRENT ADDRESS?',
  skipPreviousAddress = false,
  country,
  ...rest
}: TimeAtAddressProps & BoxProps) => {
  const prefix = isCobuyer ? 'cobuyer_' : '';
  const years = values[`${prefix}primary_residence_years`];
  const months = values[`${prefix}primary_residence_months`] ?? 0;
  const [showPreviousResidence, setShowPreviousResidence] = useState<boolean>(
    skipPreviousAddress ? false : shouldRequirePrevious(years, months),
  );

  return (
    <Box {...rest}>
      <Label mb={3}>{title}</Label>
      <MonthsAndYears
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setShow={skipPreviousAddress ? () => {} : setShowPreviousResidence}
        yearsName={`${prefix}primary_residence_years`}
        monthsName={`${prefix}primary_residence_months`}
        values={values}
        mb={showPreviousResidence ? 4 : 0}
      />
      <Collapse in={showPreviousResidence} animateOpacity>
        <Box
          w="100%"
          mb={2}
          p="8px"
          borderRadius="xl"
          bgColor="beige"
          fontSize={{ base: '10px', md: '12px' }}
        >
          {isCobuyer && firstName ? (
            <>
              {firstName} has lived here for <b>less than two years</b>. Where did they live before?{' '}
            </>
          ) : (
            <>
              You've lived here for <b>less than two years</b>. Where did you live before?{' '}
            </>
          )}
          <TextWithTooltip
            textProps={{
              fontSize: { base: '10px', md: '12px' },
              cursor: showPreviousResidence ? 'help' : 'default',
            }}
            label="To assess risk (and payment history), lenders prefer looking at more than two years' worth of data."
          >
            (Why does this matter?)
          </TextWithTooltip>
        </Box>
        <Box overflow="hidden" mb={8}>
          <Label>PREVIOUS ADDRESS</Label>
          <AddressInput
            prefix={`${prefix}previous`}
            dropLabel
            autoCompleteDisabled
            country={country}
          />
          <Label mt={6} mb={3}>
            TIME AT PREVIOUS ADDRESS?
          </Label>
          <MonthsAndYears
            yearsName={`${prefix}secondary_residence_years`}
            monthsName={`${prefix}secondary_residence_months`}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default TimeAtAddress;
